"use client";

import { createContext } from "react";

import type {
  ConsentChoice,
  ConsentScope,
  ConsentState,
  ConsentStatus,
} from "@/core/consent/domain/consent.types";
import { reopenConsentBanner } from "@/core/consent/engine/consent.runtime";

/**
 * Which tier produced `state`: the engine's resolved decision, or the
 * all-false default while nothing has resolved.
 */
export type ConsentSource = "cmp" | "default";

/** Every scope declined — what consumers see until a decision exists. */
export const defaultConsentState: ConsentState = {
  analytics: false,
  advertising: false,
  dataSharing: false,
  functional: false,
};

export type ConsentContextValue = {
  /** The effective decision per scope (a GPC clamp already applied). */
  readonly state: ConsentState;
  readonly source: ConsentSource;
  /**
   * `false` until the engine has resolved. A resolved status IS the visitor's
   * decision (or a restored one), so `isResolved && !state[scope]` is a real
   * decline.
   */
  readonly isResolved: boolean;
  /**
   * The engine's full status — provenance, notice version, GPC — for the few
   * consumers that need more than the decision. `null` while no engine is
   * mounted (server render, the first client paint), which
   * is NOT a decision: treat it exactly like `unresolved`.
   */
  readonly status: ConsentStatus | null;
  readonly getConsent: () => ConsentState;
  readonly hasConsent: (scope: ConsentScope) => boolean;
  /**
   * Tri-state for wire contracts: `true`/`false` is an explicit decision (an
   * answer, or GPC on a governed scope), `null` means not answered yet.
   */
  readonly getConsentChoice: (scope: ConsentScope) => ConsentChoice;
  /**
   * Reopen the banner (or its preferences dialog) so the visitor can revisit
   * a decision in either direction. Safe before the CMP has loaded — queued.
   */
  readonly reopenBanner: () => void;
};

// The default carries a working `reopenBanner` so a consumer rendered outside
// the provider (a test, an isolated story) still reaches the runtime's queue.
export const ConsentContext = createContext<ConsentContextValue>({
  state: defaultConsentState,
  source: "default",
  isResolved: false,
  status: null,
  getConsent: () => defaultConsentState,
  hasConsent: () => false,
  getConsentChoice: () => null,
  reopenBanner: reopenConsentBanner,
});
